/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */
import {
    ISettingGroupDefinition,
    SettingGroupDefinition,
    StringResource
} from "@nuance/hierarchical-settings-ui-domain";
import { ILogger, getLogger } from "@nuance/hux-diagnostics";
import { IGridItem } from "../../Grid/GridModels";
import { CmdBuilder, IViewModel } from "@nuance/hap-components";
import { IAdvancedViewViewModel } from "../AdvancedViewViewModels";

const logger: ILogger = getLogger();
export interface IStringViewState {
    definition: ISettingGroupDefinition;
    draftDefinition: ISettingGroupDefinition;
    editMode: boolean;
    isDirty: boolean;
    saved: boolean;
    missingLanguage: boolean;
}

export interface IStringViewProps {
    definition: ISettingGroupDefinition;
    advancedViewVM: IAdvancedViewViewModel;
}

/**
 * StringView commands and command handler
 */
export class StringViewCommands {
    constructor() {
        const cb = new CmdBuilder();
        const cmds = {
            addLanguage: cb.bindCmd(this.addLanguage),
            onTextChanged: cb.bindCmd(this.onTextChangedHandler),
            onItemClick: cb.bindCmdNP(this.onItemClickHandler),
            onSave: cb.bindCmdNP(this.saveHandler),
            onCancel: cb.bindCmdNP(this.cancelHandler),
            onDismissMsg: cb.bindCmdNP(this.dismissMsgHandler)
        };

        this.Cmds = cmds;
        this.Reducer = cb.buildReducer<IStringViewState>(cmds);
    }

    readonly Cmds;
    readonly Reducer;

    /////////////////////////////////////////////////////////////////
    // Command handlers
    /////////////////////////////////////////////////////////////////

    addLanguage = (state: IStringViewState, language: string = "fr-FR") => {
        logger.trackEvent("Add language");
        const sg = new SettingGroupDefinition(state.draftDefinition);
        const json = sg.UI.toJSON();
        const origStringResources = json.StringResources;

        const englishStrings = origStringResources.filter(s => {
            return s.Language === "en-US";
        });

        // check each English string for the matching language string
        // if not there add it
        englishStrings.forEach(resource => {
            if (!this.hasTranslation(sg, resource.Id, language)) {
                const sr = new StringResource(resource.Id, language);
                sg.UI.AddStringResource(sr);
            }
        });

        state.draftDefinition = sg.toISettingGroupDefinition();
        state.isDirty = true;
        state.editMode = true;
        state.saved = false;
        return state;
    };

    hasMissingTranslations = (
        definition: ISettingGroupDefinition,
        language: string = "fr-FR"
    ): boolean => {
        const sg = new SettingGroupDefinition(definition);
        const json = sg.UI.toJSON();
        const origStringResources = json.StringResources;
        const englishStrings = origStringResources.filter(s => {
            return s.Language === "en-US";
        });

        // check each English string for missing language string
        return englishStrings.some(resource => {
            return !this.hasTranslation(sg, resource.Id, language);
        });
    };

    private hasTranslation = (
        sg: SettingGroupDefinition,
        stringId: string,
        language: string
    ): boolean => {
        const mapKey = StringResource.CreateMapKey(stringId, language);
        const resource = sg.UI.StringResources.get(mapKey);
        return resource !== undefined;
    };

    onTextChangedHandler = (state: IStringViewState, change: { item: IGridItem; text: string }) => {
        const {
            item: { id, Language },
            text
        } = change;

        // replace old resource with new value but don't save to definition
        const sg = new SettingGroupDefinition(state.draftDefinition);
        if (!sg.UI.UpdateStringResourceText(id, text, Language.toString())) {
            logger.logError("Error updating string resource", { id });
            throw new Error("Error updating string resource");
        }
        state.draftDefinition = sg.toISettingGroupDefinition();
        state.isDirty = true;
        state.saved = false;

        return state;
    };

    onItemClickHandler = (state: IStringViewState) => {
        state.editMode = true;
        return state;
    };

    saveHandler = (state: IStringViewState) => {
        state.definition = state.draftDefinition;
        state.editMode = false;
        state.isDirty = false;
        state.saved = true;
        if (state.missingLanguage) {
            state.missingLanguage = this.hasMissingTranslations(state.definition);
        }
        return state;
    };

    cancelHandler(state: IStringViewState) {
        state.draftDefinition = state.definition;
        state.editMode = false;
        state.isDirty = false;
        return state;
    }

    dismissMsgHandler(state: IStringViewState) {
        state.saved = false;
        return state;
    }
}

type IStringViewCommands = StringViewCommands["Cmds"];

/**
 * StringViewViewModel interface
 */
export type IStringViewViewModel = IViewModel<IStringViewState, IStringViewCommands>;
